import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';

export type KnowledgeSection = 'property' | 'projects' | 'people' | 'site' | 'documents' | 'care' | 'finance';

type KnowledgeRecord = {
  id: string;
  property_id: string;
  section: KnowledgeSection;
  record_type: string;
  title: string;
  summary: string | null;
  content_text: string | null;
  source_label: string | null;
  source_url: string | null;
  status: 'draft' | 'active' | 'verified' | 'superseded';
  verification: 'owner_supplied' | 'source_record' | 'field_observed' | 'professionally_verified' | 'authority_record';
  visibility: 'owner_only' | 'property_members';
  effective_date: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
};

const sectionCopy: Record<KnowledgeSection, { title: string; introduction: string; types: string[] }> = {
  property: {
    title: 'Property identity + ownership record',
    introduction: 'Capture the facts, systems and decisions that should remain with the property through projects and ownership changes.',
    types: ['Property identity', 'Ownership + transfer', 'Building or site system', 'Condition + risk', 'Long-term intent', 'Other property record']
  },
  projects: {
    title: 'Project decisions + delivery record',
    introduction: 'Keep scopes, decisions, approvals, milestones and closeout knowledge readable beside the project itself.',
    types: ['Project decision', 'Scope + estimate', 'Approval', 'Milestone', 'Change record', 'Closeout record']
  },
  people: {
    title: 'Relationship + responsibility record',
    introduction: 'Document who knows the property, what they are responsible for and why access or continuity matters.',
    types: ['Relationship note', 'Vendor + professional', 'Access decision', 'Responsibility', 'Contact history', 'Transition note']
  },
  site: {
    title: 'Site intelligence sources',
    introduction: 'Organize zoning, parcel, water, terrain, access and other source material without presenting preliminary context as verified advice.',
    types: ['Zoning + ordinance', 'Parcel + deed', 'Water + wastewater', 'Terrain + drainage', 'Flood + wetlands', 'Solar + climate', 'Access + utilities', 'Other site source']
  },
  documents: {
    title: 'Document context + online sources',
    introduction: 'Add a readable explanation or authoritative online link beside uploaded property files.',
    types: ['Document link', 'Permit + approval', 'Deed + covenant', 'Plan + map', 'Estimate + contract', 'Photograph + evidence', 'Other document context']
  },
  care: {
    title: 'Care + operating knowledge',
    introduction: 'Preserve service knowledge, warranties, seasonal routines and system-specific instructions for the next responsible person.',
    types: ['Care instruction', 'Warranty + service', 'Seasonal task', 'Inspection finding', 'Repair history', 'Emergency procedure']
  },
  finance: {
    title: 'Funding + payment context',
    introduction: 'Keep planning assumptions, grant requirements and vendor payment context beside the ledger. These records do not move money.',
    types: ['Funding source', 'Allocation note', 'Grant requirement', 'Vendor payment context', 'Budget decision', 'Other financial context']
  }
};

const emptyDraft = (section: KnowledgeSection, recordType: string): Omit<KnowledgeRecord, 'id' | 'property_id' | 'created_at' | 'updated_at' | 'archived_at'> => ({
  section,
  record_type: recordType,
  title: '',
  summary: '',
  content_text: '',
  source_label: '',
  source_url: '',
  status: 'draft',
  verification: 'owner_supplied',
  visibility: 'property_members',
  effective_date: ''
});

const label = (value: string) => value.replaceAll('_', ' ');
const isWebUrl = (value: string) => !value || /^https?:\/\/[^\s]+$/i.test(value);

export function PropertyKnowledgeWorkspace({ propertyId, section, canManage = false }: { propertyId?: string; section: KnowledgeSection; canManage?: boolean }) {
  const copy = sectionCopy[section];
  const [rows, setRows] = useState<KnowledgeRecord[]>([]);
  const [editing, setEditing] = useState<KnowledgeRecord | null>(null);
  const [draft, setDraft] = useState(() => emptyDraft(section, copy.types[0]));
  const [showForm, setShowForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<Array<{ id: string; action: string; changed_at: string }>>([]);

  async function load() {
    if (!supabase || !propertyId) { setRows([]); return; }
    const { data, error } = await supabase.from('property_knowledge_records').select('*').eq('property_id', propertyId).eq('section', section).order('updated_at', { ascending: false });
    if (error) throw error;
    setRows((data ?? []) as KnowledgeRecord[]);
  }

  useEffect(() => {
    setDraft(emptyDraft(section, copy.types[0]));
    setEditing(null); setSelected([]); setMessage('');
    void load().catch(error => setMessage(error instanceof Error ? error.message : 'Records could not be loaded.'));
  }, [propertyId, section]);

  useEffect(() => {
    if (!supabase || !editing) { setHistory([]); return; }
    let active = true;
    void supabase.from('record_change_history').select('id,action,changed_at').eq('record_kind', 'property_knowledge_records').eq('record_id', editing.id).order('changed_at', { ascending: false }).limit(20).then(({ data }) => { if (active) setHistory(data ?? []); });
    return () => { active = false; };
  }, [editing?.id]);

  const visible = useMemo(() => rows.filter(row => Boolean(row.archived_at) === showArchived && JSON.stringify(row).toLowerCase().includes(query.trim().toLowerCase())), [rows, showArchived, query]);
  const selectedRows = rows.filter(row => selected.includes(row.id));
  const selectedDraftsOnly = selectedRows.length > 0 && selectedRows.every(row => row.status === 'draft');

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!supabase || !propertyId || !canManage) return;
    if (!isWebUrl(draft.source_url ?? '')) { setMessage('Use a complete http or https source link.'); return; }
    setBusy(true); setMessage('Saving the property record…');
    try {
      const { error } = await supabase.from('property_knowledge_records').insert({
        property_id: propertyId,
        ...draft,
        summary: draft.summary || null,
        content_text: draft.content_text || null,
        source_label: draft.source_label || null,
        source_url: draft.source_url || null,
        effective_date: draft.effective_date || null
      });
      if (error) throw error;
      setDraft(emptyDraft(section, copy.types[0])); setShowForm(false); await load(); setMessage('Record saved.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'The record could not be saved.'); }
    finally { setBusy(false); }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!supabase || !editing || !canManage) return;
    if (!isWebUrl(editing.source_url ?? '')) { setMessage('Use a complete http or https source link.'); return; }
    setBusy(true); setMessage('Saving changes…');
    try {
      const { error } = await supabase.from('property_knowledge_records').update({
        section: editing.section,
        record_type: editing.record_type,
        title: editing.title,
        summary: editing.summary || null,
        content_text: editing.content_text || null,
        source_label: editing.source_label || null,
        source_url: editing.source_url || null,
        status: editing.status,
        verification: editing.verification,
        visibility: editing.visibility,
        effective_date: editing.effective_date || null
      }).eq('id', editing.id);
      if (error) throw error;
      await load(); setEditing(null); setMessage('Changes saved.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Changes could not be saved.'); }
    finally { setBusy(false); }
  }

  async function mutate(action: 'archive' | 'restore' | 'delete', ids: string[]) {
    if (!supabase || !ids.length || !canManage) return;
    if (action === 'delete' && !window.confirm(`Permanently delete ${ids.length} draft record(s)?`)) return;
    setBusy(true); setMessage(action === 'delete' ? 'Deleting drafts…' : 'Updating records…');
    try {
      const request = action === 'delete'
        ? supabase.from('property_knowledge_records').delete().in('id', ids).eq('status', 'draft')
        : supabase.from('property_knowledge_records').update({ archived_at: action === 'archive' ? new Date().toISOString() : null }).in('id', ids);
      const { error } = await request;
      if (error) throw error;
      await load(); setSelected([]); setEditing(null); setMessage(action === 'delete' ? 'Draft records deleted.' : action === 'archive' ? 'Records archived.' : 'Records restored.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Records could not be updated.'); }
    finally { setBusy(false); }
  }

  if (!propertyId) return null;
  return <section className="panel knowledge-workspace">
    {!editing && <>
    <div className="panel-heading"><div><span className="pill orange">Property OS workspace</span><h2>{copy.title}</h2><p>{copy.introduction}</p></div>{canManage && <button className="button primary" onClick={() => setShowForm(value => !value)}>{showForm ? 'Close form' : '+ Add record'}</button>}</div>
    <div className="knowledge-boundary"><strong>Readable now, structured for deeper search later.</strong><span>Titles, descriptions, source links and detailed text are indexed inside this property Ring. Future FM.OS search can use these records without changing their ownership or verification labels.</span></div>
    {showForm && canManage && <form className="record-details" onSubmit={create}>
      <h3>Add a record</h3><RecordFields value={draft} types={copy.types} onChange={setDraft} />
      <div className="button-row"><button className="button primary" disabled={busy}>{busy ? 'Saving…' : 'Save record'}</button><button type="button" className="button secondary" onClick={() => setShowForm(false)}>Cancel</button></div>
    </form>}
    <div className="workspace-tools"><input aria-label="Search this workspace" placeholder="Search titles, notes and sources" value={query} onChange={event => { setQuery(event.target.value); setSelected([]); }} /><label><input type="checkbox" checked={showArchived} onChange={event => { setShowArchived(event.target.checked); setSelected([]); setEditing(null); }} /> Show archived</label><button className="button secondary" disabled={busy} onClick={() => void load().catch(error => setMessage(error.message))}>Refresh</button></div>
    {canManage && <div className="button-row"><strong>{selected.length} selected</strong><button className="button secondary" disabled={busy || !selected.length} onClick={() => void mutate(showArchived ? 'restore' : 'archive', selected)}>{showArchived ? 'Restore selected' : 'Archive selected'}</button>{selectedDraftsOnly && <button className="button secondary" disabled={busy} onClick={() => void mutate('delete', selected)}>Delete selected drafts</button>}</div>}
    <div className="knowledge-list">{visible.map(row => <article className="knowledge-row" key={row.id}>
      {canManage && <input aria-label={`Select ${row.title}`} type="checkbox" checked={selected.includes(row.id)} onChange={event => setSelected(ids => event.target.checked ? [...ids, row.id] : ids.filter(id => id !== row.id))} />}
      <button className="record-title" onClick={() => setEditing({ ...row })}><strong>{row.title}</strong><small>{row.record_type} · {label(row.verification)} · {row.status}</small>{row.summary && <span>{row.summary}</span>}</button>
      {row.source_url && isWebUrl(row.source_url) && <a className="button secondary" href={row.source_url} target="_blank" rel="noreferrer">Open source</a>}
      <button className="button secondary" onClick={() => setEditing({ ...row })}>Read details</button>
      {canManage && <button className="button secondary" disabled={busy} onClick={() => void mutate(showArchived ? 'restore' : 'archive', [row.id])}>{showArchived ? 'Restore' : 'Archive'}</button>}
      {canManage && row.status === 'draft' && <button className="button secondary" disabled={busy} onClick={() => void mutate('delete', [row.id])}>Delete draft</button>}
    </article>)}</div>
    {!visible.length && <div className="empty-state compact"><h3>{showArchived ? 'No archived records' : 'No records here yet'}</h3><p>Add the first fact, source link, decision or operating note for this part of the property.</p></div>}
    </>}
    {editing && <form className="record-details" onSubmit={save}>
      <div className="panel-heading"><div><span className="pill">{editing.record_type}</span><h3>{editing.title}</h3></div><button type="button" className="button secondary" disabled={busy} onClick={() => setEditing(null)}>Back to list</button></div>
      {canManage ? <RecordFields value={editing} types={copy.types} onChange={setEditing} /> : <div className="record-reading"><p>{editing.summary}</p><div>{editing.content_text}</div></div>}
      <div className="button-row">{canManage && <button className="button primary" disabled={busy}>Save changes</button>}{editing.source_url && isWebUrl(editing.source_url) && <a className="button secondary" href={editing.source_url} target="_blank" rel="noreferrer">Open source</a>}</div>
      <h3>Change history</h3>{history.length ? history.map(item => <p key={item.id}>{label(item.action)} · {new Date(item.changed_at).toLocaleString()}</p>) : <p>No edits, archive actions or restorations recorded yet.</p>}
    </form>}
    <p role="status">{message}</p>
  </section>;
}

function RecordFields<T extends Partial<KnowledgeRecord>>({ value, types, onChange }: { value: T; types: string[]; onChange: (value: T) => void }) {
  const set = (key: keyof KnowledgeRecord, next: string) => onChange({ ...value, [key]: next } as T);
  return <div className="form-grid">
    <label>Record type<select value={value.record_type ?? types[0]} onChange={event => set('record_type', event.target.value)}>{types.map(type => <option key={type}>{type}</option>)}</select></label>
    <label>Title<input value={value.title ?? ''} onChange={event => set('title', event.target.value)} required maxLength={180} /></label>
    <label className="full-field">Plain-language summary<textarea value={value.summary ?? ''} onChange={event => set('summary', event.target.value)} maxLength={1200} placeholder="What should someone understand before opening the full source?" /></label>
    <label className="full-field">Detailed record or extracted text<textarea className="long-text" value={value.content_text ?? ''} onChange={event => set('content_text', event.target.value)} placeholder="Paste relevant ordinance language, operating instructions, decisions, history, or notes here." /></label>
    <label>Source label<input value={value.source_label ?? ''} onChange={event => set('source_label', event.target.value)} placeholder="Town ordinance, owner note, engineer…" /></label>
    <label>Online source link<input type="url" value={value.source_url ?? ''} onChange={event => set('source_url', event.target.value)} placeholder="https://…" /></label>
    <label>Effective or observed date<input type="date" value={value.effective_date ?? ''} onChange={event => set('effective_date', event.target.value)} /></label>
    <label>Verification<select value={value.verification ?? 'owner_supplied'} onChange={event => set('verification', event.target.value)}><option value="owner_supplied">Owner supplied</option><option value="source_record">Source record</option><option value="field_observed">Field observed</option><option value="professionally_verified">Professionally verified</option><option value="authority_record">Authority record</option></select></label>
    <label>Visibility<select value={value.visibility ?? 'property_members'} onChange={event => set('visibility', event.target.value)}><option value="owner_only">Owner only</option><option value="property_members">Property members</option></select></label>
    <label>Status<select value={value.status ?? 'draft'} onChange={event => set('status', event.target.value)}><option value="draft">Draft</option><option value="active">Active</option><option value="verified">Verified</option><option value="superseded">Superseded</option></select></label>
  </div>;
}
